import React, { useState, useCallback, useMemo } from 'react';
import { Typography, Grid, Menu, MenuItem, IconButton, MoreHorizIcon, Alert } from '@rbx/ui';
import { useTranslation } from '@rbx/intl';
import useSideBarContent from '@modules/ui/shared/hooks/useSideBarContent';
import {
  extractCourseTitle,
  getCourseByCourseId,
  COURSE_PATH_SEGMENTS_INDEX,
} from '@modules/learning/utils/courseUtils';
import { getLocalLearningProgress } from '@modules/learning/utils/localStorageSync';
import learningClient from '@modules/clients/learningClient';
import { Status } from '@modules/learning/types/learning';
import { courseConfigs } from '@modules/learning/constants/courseConfigs';
import unifiedLoggerClient from '@modules/eventStream/implementations/unifiedLoggerClient';
import DocsCoursesEvent from '@modules/eventStream/enum/DocsCoursesEvent';
import useCurrentLocale from '@modules/localization/hooks/useCurrentLocale';
import ResetProgressDialog from './ResetProgressDialog';

interface CourseTitleWithMenuProps {
  className?: string;
  showResetAlert: boolean;
  onResetAlertChange: (show: boolean) => void;
}

export default function CourseTitleWithMenu({
  className,
  showResetAlert,
  onResetAlertChange,
}: CourseTitleWithMenuProps) {
  const { translate } = useTranslation();
  const { treeListData } = useSideBarContent();
  const { targetLocale } = useCurrentLocale();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [resetAlertType, setResetAlertType] = useState<'success' | 'error'>('success');

  const courseTitle = extractCourseTitle(treeListData, translate);

  const courseId = useMemo(() => {
    if (!treeListData || treeListData.length === 0) {
      return null;
    }

    const firstNode = treeListData[0]?.nodes?.[0];
    if (!firstNode?.path) {
      return null;
    }

    const pathParts = firstNode.path.split('/');
    if (pathParts.length <= COURSE_PATH_SEGMENTS_INDEX) {
      return null;
    }

    return pathParts[COURSE_PATH_SEGMENTS_INDEX];
  }, [treeListData]);

  const hasProgress = useMemo(() => {
    const lessonStatuses = getLocalLearningProgress();
    return Object.values(lessonStatuses).some(
      (status) =>
        status === Status.STATUS_ENUM_COMPLETED || status === Status.STATUS_ENUM_IN_PROGRESS,
    );
  }, []);

  const onResetProgress = useCallback(async (courseIdToReset: string): Promise<boolean> => {
    if (!courseIdToReset) return false;

    try {
      const courseConfig = courseConfigs.find((config) => config.id === courseIdToReset);
      if (!courseConfig) return false;

      const template = getCourseByCourseId(courseConfig.courseId);
      if (template) {
        const resetPromises = template.milestones.flatMap((milestone) =>
          milestone.lessons.map((lesson) =>
            learningClient.updateLessonProgress(lesson.path, Status.STATUS_ENUM_NOT_STARTED),
          ),
        );

        await Promise.all(resetPromises);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to reset course progress:', error);
      return false;
    }
  }, []);

  const handleMoreClick = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    e.preventDefault();

    unifiedLoggerClient.logClickEvent({
      eventName: DocsCoursesEvent.clickResetProgressMenu,
      parameters: {
        locale: targetLocale ? String(targetLocale) : '',
        courseId: courseId || '',
      },
    });

    setAnchorEl(e.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleResetClick = () => {
    setAnchorEl(null);
    setConfirmDialogOpen(true);
  };

  const handleConfirmReset = useCallback(async () => {
    setConfirmDialogOpen(false);
    if (courseId && onResetProgress) {
      const success = await onResetProgress(courseId);
      setResetAlertType(success ? 'success' : 'error');
      onResetAlertChange(true);
      setTimeout(() => onResetAlertChange(false), 5000);
    }
  }, [courseId, onResetProgress, onResetAlertChange]);

  const handleCancelReset = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <React.Fragment>
      {showResetAlert && (
        <Alert
          severity={resetAlertType}
          sx={{
            position: 'fixed',
            left: '50%',
            bottom: '50px',
            transform: 'translateX(-50%)',
            zIndex: 1000,
          }}>
          {resetAlertType === 'success'
            ? translate('Message.CourseProgressResetSuccess')
            : translate('Message.CourseProgressResetError')}
        </Alert>
      )}
      <Grid
        item
        className={className}
        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography component='h4' variant='overline' color='secondary'>
          {courseTitle || translate('Label.Course')}
        </Typography>
        <IconButton
          onClick={handleMoreClick}
          size='small'
          disabled={!hasProgress}
          aria-label={translate('Label.CourseOptions')}
          color='secondary'>
          <MoreHorizIcon />
        </IconButton>
      </Grid>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}>
        <MenuItem onClick={handleResetClick}>{translate('Label.ResetProgress')}</MenuItem>
      </Menu>
      <ResetProgressDialog
        open={confirmDialogOpen}
        onConfirm={handleConfirmReset}
        onCancel={handleCancelReset}
      />
    </React.Fragment>
  );
}
